import { useState } from 'react'
import { ButtonPrimary } from '../../buttons/ButtonPrimary'
import { ButtonSecondary } from '../../buttons/ButtonSecondary'
import { BatchWidgetCard } from '../../reusable/batchWidgetCard/BatchWidgetCard'
import { BatchRunsTable } from './BatchRunsTable'
import { BatchRowsTable } from './BatchRowsTable'
import { useViewThruBatchUpload } from './hooks/useViewThruBatchUpload/useViewThruBatchUpload'
import { BatchRun } from './shared'
import { COLUMN_GROUPS, COLUMN_PILL_CLASS, ColumnGroup } from './columnGroups'
import { ConfirmCoverageBody } from './ConfirmCoverageBody'
import styles from './styles.module.scss'

const ColumnGroupBlock = ({ group }: { group: ColumnGroup }) => {
    const [open, setOpen] = useState(!group.collapsible)
    const pills = (
        <span className={styles.columnPills}>
            {group.columns.map((column) => (
                <span
                    key={column.name}
                    className={styles[COLUMN_PILL_CLASS[column.variant]]}
                    title={column.variant === 'requiredBureau' ? 'Required for TransUnion' : undefined}
                >
                    {column.name}
                </span>
            ))}
        </span>
    )

    if (!group.collapsible) {
        return (
            <div className={styles.columnGroup}>
                <span className={styles.columnGroupLabel}>{group.label}</span>
                {group.hint && <span className={styles.columnGroupHint}>{group.hint}</span>}
                {pills}
            </div>
        )
    }

    return (
        <div className={styles.columnGroup}>
            <button
                type="button"
                className={styles.columnGroupToggle}
                aria-expanded={open}
                onClick={(event) => {
                    event.stopPropagation()
                    setOpen((value) => !value)
                }}
            >
                <span className={styles.columnGroupLabel}>{group.label}</span>
                <span className={styles.columnGroupToggleHint}>
                    {open ? 'Hide' : `Show ${group.columns.length} columns`}
                </span>
            </button>
            {open && (
                <>
                    {group.hint && <span className={styles.columnGroupHint}>{group.hint}</span>}
                    {pills}
                </>
            )}
        </div>
    )
}

export const ViewThruBatchView = () => {
    const [openBatch, setOpenBatch] = useState<BatchRun | null>(null)
    const upload = useViewThruBatchUpload()
    const coverage = upload.coverage

    if (openBatch) {
        return (
            <div className={styles.page}>
                <BatchRowsTable
                    batchId={openBatch.id}
                    initialBatch={openBatch}
                    onBack={() => setOpenBatch(null)}
                    onResume={(run) => {
                        setOpenBatch(null)
                        void upload.resumeExistingBatch(run)
                    }}
                    resumeDisabled={upload.busy}
                />
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <input
                ref={upload.fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className={styles.fileInput}
                onChange={(event) => upload.acceptSpreadsheet(event.target.files?.[0])}
            />

            <div className={styles.uploadColumn}>
                <label className={styles.originField}>
                    <span className={styles.originLabel}>Origin</span>
                    <input
                        className={styles.originInput}
                        list="viewthru-batch-origins"
                        value={upload.origin}
                        onChange={(event) => upload.setOrigin(event.target.value)}
                        placeholder="Name where this data came from"
                        autoComplete="off"
                        disabled={upload.busy}
                    />
                    <datalist id="viewthru-batch-origins">
                        {upload.originNames.map((name) => (
                            <option key={name} value={name} />
                        ))}
                    </datalist>
                    <span className={styles.originHint}>
                        Pick an existing group or type a new name. Required before validating. Each upload
                        is its own run — origin is a label, not a merge into earlier batches.
                    </span>
                </label>

                <BatchWidgetCard
                    step={upload.step}
                    title="Batch Credit Check"
                    subtitle={
                        <div className={styles.columnGuide}>
                            Choose or drop a CSV or Excel file.
                            {COLUMN_GROUPS.map((group) => (
                                <ColumnGroupBlock key={group.label} group={group} />
                            ))}
                        </div>
                    }
                    onChoose={upload.openFilePicker}
                    onFilesDropped={upload.handleFilesDropped}
                    // Preview phases control — keep the hook/plumbing; flip this
                    // when you need to walk the card through each phase again.
                    cornerActionShown={false}
                    cornerAction={
                        <button
                            type="button"
                            className={styles.mockButton}
                            onClick={upload.mocking ? upload.stopMockRun : upload.startMockRun}
                            disabled={upload.busy && !upload.mocking}
                            title="Runs the card through each phase for 5 seconds. Nothing is uploaded and no batch is created."
                        >
                            {upload.mocking ? 'Stop preview' : 'Preview phases'}
                        </button>
                    }
                    chooseAction={<ButtonSecondary onClick={upload.openFilePicker}>Choose file</ButtonSecondary>}
                    fileName={upload.fileName ?? undefined}
                    onClearFile={upload.clearFile}
                    readyAction={
                        <ButtonPrimary
                            title="Validate"
                            loading={upload.verifying && !coverage}
                            disabled={!upload.canRun}
                            onClick={upload.runBatch}
                        />
                    }
                    confirmTitle="Some required fields are missing"
                    confirmBody={coverage ? <ConfirmCoverageBody coverage={coverage} /> : null}
                    confirmAction={
                        <div className={styles.confirmActions}>
                            <ButtonSecondary onClick={upload.dismissCoverage}>Go back</ButtonSecondary>
                            <ButtonPrimary
                                title="Continue anyway"
                                loading={upload.verifying}
                                onClick={upload.confirmContinue}
                            />
                        </div>
                    }
                    runningTitle={
                        upload.phase === 'deduping'
                            ? 'Checking for duplicates...'
                            : upload.verifying
                              ? 'Verifying file...'
                              : 'Batch running...'
                    }
                    progress={upload.progress}
                    phaseSteps={upload.phaseSteps}
                    progressLabel={
                        upload.phase === 'deduping'
                            ? undefined
                            : upload.verifying
                              ? 'Checking rows and columns'
                              : upload.progressLabel
                    }
                    progressFooter={
                        upload.running && upload.phase !== 'deduping' ? (
                            <>
                                <span>{upload.elapsedLabel ? `${upload.elapsedLabel} elapsed` : ''}</span>
                                <span>
                                    {upload.finishing
                                        ? 'Finishing up'
                                        : upload.remainingLabel
                                          ? `~${upload.remainingLabel} remaining`
                                          : 'Estimating…'}
                                </span>
                            </>
                        ) : undefined
                    }
                />
            </div>

            <BatchRunsTable
                reloadToken={upload.runsReloadToken}
                optimisticRun={upload.optimisticRun}
                onViewDetails={setOpenBatch}
                onResume={upload.resumeExistingBatch}
                resumeDisabled={upload.busy}
            />
        </div>
    )
}
